import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { MaskedLine, EASE } from "./Reveal";
import { Star } from "./Logo";
import { StoreBadges } from "./StoreBadges";
import { scrollToSection } from "./Navbar";
import { useLang } from "@/i18n/LanguageContext";

const OCC_LEVELS = [2, 3, 3, 2];
const BADGE = {
  de: { label: "Deine Wirkung", cat: "Ausstrahlung", levels: ["Potenzial sichtbar", "Im Aufblühen", "Strahlend", "Absolut strahlend"] },
  en: { label: "Your presence", cat: "Radiance", levels: ["Potential showing", "Blossoming", "Radiant", "Absolutely radiant"] },
  es: { label: "Tu efecto", cat: "Resplandor", levels: ["Potencial visible", "Floreciendo", "Radiante", "Absolutamente radiante"] },
  it: { label: "La tua presenza", cat: "Splendore", levels: ["Potenziale visibile", "In fioritura", "Radiosa", "Assolutamente radiosa"] },
  fr: { label: "Ta présence", cat: "Éclat", levels: ["Potentiel visible", "En éclosion", "Rayonnante", "Absolument rayonnante"] },
};

const BadgeTile = ({ lang, occ }) => {
  const b = BADGE[lang] || BADGE.de;
  const level = OCC_LEVELS[occ];
  return (
    <div className="flex flex-col items-center gap-2.5" style={{ width: 150 }} data-testid="hero-badge-tile">
      <span className="text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", color: "#B8860B" }}>{b.label}</span>
      <AnimatePresence mode="wait">
        <motion.span
          key={occ}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-full border-[1.5px] px-3.5 py-2 font-serif-display text-[15px] font-bold leading-tight"
          style={{ borderColor: "#B8860B", background: "#F4EEE7", color: "#2B2622" }}
          data-testid="hero-badge-pill"
        >
          <Star size={13} /> {b.levels[level]}
        </motion.span>
      </AnimatePresence>
      <span className="flex items-center gap-2 text-[10px]" style={{ color: "var(--muted-warm)" }}>
        {b.cat}
        <span className="flex gap-1" style={{ color: "#B8860B" }} aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: i <= level ? "#B8860B" : "var(--line)" }} />
          ))}
        </span>
      </span>
    </div>
  );
};

const PhoneMockup = () => (
  <div
    data-testid="hero-phone-mockup"
    className="relative w-[280px] sm:w-[310px] rounded-[44px] border-[10px] shadow-2xl"
    style={{ borderColor: "var(--on-surface)", background: "var(--on-surface)", boxShadow: "0 40px 80px -30px rgba(44,39,37,0.35)" }}
  >
    <div className="overflow-hidden rounded-[34px]">
      <img
        src="/app-screens/result.webp?v=4"
        alt="Mira Mirror App: echtes Analyse-Ergebnis mit Wirkungs-Stufe Strahlend"
        width={640}
        height={1389}
        className="w-full"
        data-testid="hero-app-screenshot"
      />
    </div>
  </div>
);

export default function Hero() {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yPhone = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yRing = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="hero" className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28" data-testid="hero-section">
      <div
        className="pointer-events-none absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle, var(--rose-soft) 0%, transparent 65%)" }}
      />
      <Star className="star-slow-spin absolute top-36 left-[6%] hidden lg:block opacity-60" size={26} />
      <Star className="star-slow-spin absolute bottom-24 left-[42%] hidden lg:block opacity-40" size={18} />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-[1.15fr_1fr]">
        <div>
          <MaskedLine delay={0.05}>
            <span className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-medium uppercase" style={{ borderColor: "var(--gold)", color: "var(--camel)", letterSpacing: "0.14em" }}>
              <Star size={11} /> {t.hero.kicker}
            </span>
          </MaskedLine>

          <h1 className="mt-8 font-serif-display font-semibold leading-[1.04] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-title">
            <MaskedLine delay={0.15}>{t.hero.h1[0]}</MaskedLine>
            <MaskedLine delay={0.28}>
              <em className="not-italic" style={{ color: "var(--camel)" }}>{t.hero.h1[1]}</em>
            </MaskedLine>
            <MaskedLine delay={0.41}>{t.hero.h1[2]}</MaskedLine>
          </h1>

          <motion.p
            className="mt-7 max-w-lg text-base md:text-lg leading-relaxed"
            style={{ color: "var(--muted-warm)" }}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8, ease: EASE }}
            data-testid="hero-subline"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            className="mt-9 flex flex-wrap items-center gap-5"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
          >
            <StoreBadges height={46} />
            <Link
              to="/mira-plus"
              data-testid="hero-plus-cta"
              className="pill-btn inline-flex items-center gap-2 rounded-full px-7 py-3 text-xs font-bold uppercase text-white transition-transform duration-300 hover:scale-[1.04]"
              style={{ background: "var(--camel)", letterSpacing: "0.14em" }}
            >
              {t.hero.plusCta} →
            </Link>
            <button
              data-testid="hero-boutique-cta"
              onClick={() => scrollToSection("boutiquen")}
              className="pill-btn inline-flex items-center gap-2 rounded-full border-2 px-7 py-3 text-xs font-bold uppercase transition-colors duration-300 hover:text-white"
              style={{ borderColor: "var(--camel)", color: "var(--camel)", letterSpacing: "0.14em" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--camel)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              {t.hero.boutiqueCta} →
            </button>
          </motion.div>

          <motion.p
            className="mt-10 flex items-center gap-2 text-xs sm:text-sm uppercase"
            style={{ color: "var(--muted-warm)", letterSpacing: "0.12em" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            data-testid="hero-trust-line"
          >
            <Star size={12} /> {t.hero.trust}
          </motion.p>
        </div>

        <div className="relative flex justify-center lg:justify-end pr-0 lg:pr-10">
          <motion.div
            style={{ y: yPhone }}
            initial={{ opacity: 0, y: 60, rotate: 2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1.1, ease: EASE }}
          >
            <PhoneMockup />
          </motion.div>
          <motion.div
            className="absolute -left-2 sm:left-2 lg:-left-10 top-10 rounded-3xl border p-4 shadow-xl backdrop-blur-md"
            style={{ y: yRing, background: "rgba(252,250,248,0.92)", borderColor: "var(--line)" }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            data-testid="hero-score-ring"
          >
            <BadgeTile lang={lang} occ={0} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
